import { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Textarea } from "@/components/ui/TextArea";
import { Label } from "@/components/ui/Label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/RadioGroup";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/Select";
import { Checkbox } from "@/components/ui/CheckBox";
import {
  Sparkles,
  FileText,
  Save,
  Settings,
  Eye,
  Edit2,
  Trash2,
  Plus,
  GripVertical,
  X,
  ArrowLeft,
  ChevronDown,
  Mic,
  MicOff,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/DropdownMenu";
import { FormStatus } from "@/app/shared_enums/enums";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useSpeechToText } from "@/hooks/useSpeechToText";
import useDocumentTitle from "@/hooks/UseDocumentTitle";
import {
  useGetFormFieldsQuery,
  useCreateFormMutation,
  useUpdateFormMutation,
  useGetFormBySlugQuery,
  useGenerateAIMutation,
} from "@/services/FormBuilderApi";
import { capitalize, createTagTreeFromTagNodes } from "../../../utils/Helper";
import { useSearchParams, useNavigate, UNSAFE_NavigationContext as NavigationContext } from "react-router-dom";
import { useContext } from "react";
import { Switch } from "@/components/ui/Switch";

// ============================================================================
// TYPES
// ============================================================================

export interface Field {
  id: string;
  type: string;
  label: string;
  placeholder?: string;
  helpText?: string;
  required?: boolean;
  options?: string[];
  conditions?: Condition[];
  min?: number;
  max?: number;
  startDateTime?: string; // For date/datetime min validation
  endDateTime?: string;   // For date/datetime max validation
  rows?: number;
  accept?: string;
  order?: number;
  apiIntegration?: ApiIntegration;
  sectionId?: string; // which group this field belongs to
  sectionTitle?: string; // displayed as header (editable)
  layout?: {
    columns: number; // 1, 2, 3, or 4 → how many fields share this row
    order: number; // 0, 1, 2, 3 → position in the row
  };
}

interface Condition {
  id: string;
  sourceField: string;
  operator: string;
  value?: string;
  action: string;
}

interface ApiIntegration {
  enabled: boolean;
  endpoint: string;
  method: string;
  token?: string;
  responseMappings: ResponseMapping[];
}

interface ResponseMapping {
  id: string;
  responsePath: string;
  targetField: string;
}

interface GeneratedForm {
  id?: string;
  name: string;
  description: string;
  slug?: string;
  status?: string;
  fields: Field[];
}

// ============================================================================
// CUSTOM HOOKS
// ============================================================================

const ITS_URL = `${import.meta.env.VITE_API_URL?.replace(/\/$/, "")}/live-queue/users/search?username={value}&org_id=1`;

const ITS_FIELDS = [
  { name: "first_name", label: "First Name" },
  { name: "last_name", label: "Last Name" },
  { name: "email", label: "Email" },
  { name: "phone_num", label: "Phone Number" },
  { name: "gender", label: "Gender" },
];
type FromValues = Omit<Form, 'tag_path'> & {tag_path: number[] | undefined}
const initialForm = {
  name: "",
  description: "",
  fields: [],
  status: FormStatus.Draft,
  tag_path: undefined,
  requires_login: false,
  show_on_user_portal: false
}
const useFormData = (slug: string | null, setSections: any) => {
  const { userInfo } = useSelector((state: RootState) => state.auth);
  const userOrgId = Number(userInfo?.org_id);
  const [generatedForm, setGeneratedForm] = useState<FromValues | null>(initialForm);
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [editMode, setEditMode] = useState(true);
  const [selectedField, setSelectedField] = useState<Field | null>(null);
  const [isShowSaveForm, setIsShowSaveForm] = useState(true);
  const [eventId, setEventId] = useState<number | undefined>();

  const { data: formFromApi, isLoading } = useGetFormBySlugQuery(slug!, {
    skip: !slug,
  });

  const path = formFromApi?.tag_path?.split('/').filter(Boolean).map(Number)
  const tagId = path?.[path.length - 1]
  const { data: tagById } = useGetTagByIdQuery(tagId!, {
    skip: !tagId,
  });

  const { data: eventsResponse } = useGetEventsQuery({ org_id: userOrgId });
  const events = eventsResponse?.data ?? [];

  useEffect(() => {
    if (!formFromApi || !slug || !events) return
    setIsShowSaveForm(false);

    // BUILD SECTIONS STATE FROM API DATA - THIS IS THE KEY FIX
    const sectionsMap: Record<string, { title: string; collapsed: boolean }> =
      {};
    formFromApi.fields.forEach((field) => {
      if (field.section_id && !sectionsMap[field.section_id]) {
        sectionsMap[field.section_id] = {
          title: field.section_title || "Untitled Section",
          collapsed: false,
        };
      }
    });
    setSections(sectionsMap); // <-- THIS LINE IS CRITICAL

    if (tagById)
        setEventId(tagById.event_id ?? undefined)
    setGeneratedForm({
      id: formFromApi.id,
      name: formFromApi.name,
      description: formFromApi.description || "",
      tag_path: formFromApi.tag_path?.split('/').filter(Boolean).map(Number),
      requires_login: formFromApi.requires_login,
      show_on_user_portal: formFromApi.show_on_user_portal,
      slug: formFromApi.slug,
      status: formFromApi.status,
      fields: [...formFromApi.fields]
        .sort((a: any, b: any) => a.order - b.order)
        .map((field) => ({
          id: field.field_id || field.id.toString(),
          type: field.type,
          label: field.label,
          placeholder: field.placeholder || "",
          helpText: field.helper_text || "",
          required: field.required || false,
          options: field.options || [],
          conditions: field.conditions || [],
          min: field.min ?? undefined,
          max: field.max ?? undefined,
          startDateTime: field.start_date_time || undefined,
          endDateTime: field.end_date_time || undefined,
          rows: field.rows ?? undefined,
          accept: field.accept || undefined,
          order: field.order,
          sectionId: field.section_id || undefined,
          sectionTitle: field.section_title || undefined,
          layout: field.layout || undefined,
          apiIntegration: (field.api_integration || {
            enabled: false,
            endpoint: "",
            method: "GET",
            token: "",
            responseMappings: [],
          }) as Form['fields'][number]['apiIntegration'],
        })),
    });
  }, [formFromApi, slug, events, tagById]);

  return {
    eventId,
    setEventId,
    events,
    generatedForm,
    setGeneratedForm,
    formData,
    setFormData,
    editMode,
    setEditMode,
    selectedField,
    setSelectedField,
    isShowSaveForm,
    setIsShowSaveForm,
    isLoading,
  };
};

const useDragAndDrop = () => {
  const [draggedFieldType, setDraggedFieldType] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  return { draggedFieldType, setDraggedFieldType, sensors };
};

// ============================================================================
// CONDITIONAL LOGIC HANDLERS
// ============================================================================

/**
 * Normalizes AI-generated field to ensure consistent property naming
 * Handles both camelCase and snake_case from AI responses
 */
const normalizeAIField = (field: any): Field => {
  return {
    id: field.id || field.field_id,
    type: field.type,
    label: field.label,
    placeholder: field.placeholder || "",
    helpText: field.helpText || field.helper_text || "",
    required: field.required || false,
    options: field.options || [],
    conditions: field.conditions || [],
    min: field.min ?? undefined,
    max: field.max ?? undefined,
    startDateTime: field.startDateTime || field.start_date_time || undefined,
    endDateTime: field.endDateTime || field.end_date_time || undefined,
    rows: field.rows ?? undefined,
    accept: field.accept || undefined,
    order: field.order ?? undefined,
    // Normalize section properties (prefer camelCase, fallback to snake_case)
    sectionId: field.sectionId || field.section_id || undefined,
    sectionTitle: field.sectionTitle || field.section_title || undefined,
    layout: field.layout || undefined,
    // Normalize API integration (prefer camelCase, fallback to snake_case)
    apiIntegration: field.apiIntegration || field.api_integration || {
      enabled: false,
      endpoint: "",
      method: "GET",
      token: "",
      responseMappings: [],
    },
  };
};

const validateFieldConditions = (
  newFields: Field[],
  existingFields: Field[]
): Field[] => {
  const allFields = [...existingFields, ...newFields];
  const allFieldIds = new Set(allFields.map((f) => f.id));

  return newFields.map((field) => {
    if (field.conditions) {
      const validConditions = field.conditions.filter((cond) => {
        if (!allFieldIds.has(cond.sourceField)) {
          console.warn(
            `Removing condition: sourceField "${cond.sourceField}" not found`
          );
          return false;
        }
        return true;
      });

      return {
        ...field,
        conditions: validConditions.length > 0 ? validConditions : undefined,
      };
    }
    return field;
  });
};

const evaluateFieldConditions = (
  field: Field,
  formData: Record<string, any>
): { shouldShow: boolean; isRequired: boolean } => {
  if (!field.conditions || field.conditions.length === 0) {
    return {
      shouldShow: true,
      isRequired: field.required || false,
    };
  }

  let shouldShow = true;
  let isRequired = field.required || false;

  field.conditions.forEach((condition) => {
    const sourceValue = formData[condition.sourceField];
    let conditionMet = false;

    switch (condition.operator) {
      case "equals":
        conditionMet = Array.isArray(sourceValue)
          ? sourceValue.length === 1 && sourceValue[0] === condition.value
          : sourceValue === condition.value;
        break;
      case "not_equals":
        conditionMet = Array.isArray(sourceValue)
          ? !(sourceValue.length === 1 && sourceValue[0] === condition.value)
          : sourceValue !== condition.value;
        break;
      case "includes":
        conditionMet = Array.isArray(sourceValue)
          ? sourceValue.includes(condition.value)
          : sourceValue?.toString().includes(condition.value);
        break;
      case "not_includes":
        conditionMet = Array.isArray(sourceValue)
          ? !sourceValue.includes(condition.value)
          : !sourceValue?.toString().includes(condition.value);
        break;
      case "is_empty":
        conditionMet = Array.isArray(sourceValue)
          ? sourceValue.length === 0
          : !sourceValue || sourceValue.toString().trim() === "";
        break;
      case "is_not_empty":
        conditionMet = Array.isArray(sourceValue)
          ? sourceValue.length > 0
          : sourceValue && sourceValue.toString().trim() !== "";
        break;
    }

    if (conditionMet) {
      switch (condition.action) {
        case "show":
          shouldShow = true;
          break;
        case "hide":
          shouldShow = false;
          break;
        case "require":
          isRequired = true;
          break;
        case "optional":
          isRequired = false;
          break;
      }
    } else {
      if (condition.action === "show") {
        shouldShow = false;
      } else if (condition.action === "hide") {
        shouldShow = true;
      }
    }
  });

  return { shouldShow, isRequired };
};

const handleAddCondition = (setSelectedField: (fn: (prev: any) => any) => void, setHasUnsavedChanges: (val: boolean) => void) => {
  const newCondition = {
    id: `cond_${Date.now()}`,
    sourceField: "",
    operator: "equals",
    value: "",
    action: "show",
  };

  setSelectedField((prev) => ({
    ...prev,
    conditions: [...(prev.conditions || []), newCondition],
  }));
  if (setHasUnsavedChanges) setHasUnsavedChanges(true);
};

const handleRemoveCondition = (conditionId: string, setSelectedField: (fn: (prev: any) => any) => void, setHasUnsavedChanges: (val: boolean) => void) => {
  setSelectedField((prev) => ({
    ...prev,
    conditions: (prev.conditions || []).filter((c: any) => c.id !== conditionId),
  }));
  if (setHasUnsavedChanges) setHasUnsavedChanges(true);
};

const handleConditionChange = (conditionId: string, key: string, value: any, setSelectedField: (fn: (prev: any) => any) => void, setHasUnsavedChanges: (val: boolean) => void) => {
  setSelectedField((prev) => ({
    ...prev,
    conditions: (prev.conditions || []).map((c: any) =>
      c.id === conditionId ? { ...c, [key]: value } : c
    ),
  }));
  if (setHasUnsavedChanges) setHasUnsavedChanges(true);
};

// ============================================================================
// API INTEGRATION HANDLERS
// ============================================================================

const handleToggleApi = (checked: boolean, setSelectedField: (fn: (prev: any) => any) => void, setHasUnsavedChanges: (val: boolean) => void) => {
  setSelectedField((prev) => ({
    ...prev,
    apiIntegration: {
      ...prev.apiIntegration,
      enabled: checked,
    },
  }));
  if (setHasUnsavedChanges) setHasUnsavedChanges(true);
};

const handleApiChange = (key: string, value: any, setSelectedField: (fn: (prev: any) => any) => void, setHasUnsavedChanges: (val: boolean) => void) => {
  setSelectedField((prev) => ({
    ...prev,
    apiIntegration: {
      ...prev.apiIntegration,
      [key]: value,
    },
  }));
  if (setHasUnsavedChanges) setHasUnsavedChanges(true);
};

const handleAddMapping = (setSelectedField: (fn: (prev: any) => any) => void, setHasUnsavedChanges: (val: boolean) => void) => {
  const newMapping = {
    id: `map_${Date.now()}`,
    responsePath: "",
    targetField: "",
  };
  setSelectedField((prev) => ({
    ...prev,
    apiIntegration: {
      ...prev.apiIntegration,
      responseMappings: [
        ...(prev.apiIntegration.responseMappings || []),
        newMapping,
      ],
    },
  }));
  if (setHasUnsavedChanges) setHasUnsavedChanges(true);
};

const handleRemoveMapping = (mappingId: string, setSelectedField: (fn: (prev: any) => any) => void, setHasUnsavedChanges: (val: boolean) => void) => {
  setSelectedField((prev) => ({
    ...prev,
    apiIntegration: {
      ...prev.apiIntegration,
      responseMappings: (prev.apiIntegration.responseMappings || []).filter(
        (m: any) => m.id !== mappingId
      ),
    },
  }));
  if (setHasUnsavedChanges) setHasUnsavedChanges(true);
};

const handleMappingChange = (mappingId: string, key: string, value: any, setSelectedField: (fn: (prev: any) => any) => void, setHasUnsavedChanges: (val: boolean) => void) => {
  setSelectedField((prev) => ({
    ...prev,
    apiIntegration: {
      ...prev.apiIntegration,
      responseMappings: (prev.apiIntegration.responseMappings || []).map((m: any) =>
        m.id === mappingId ? { ...m, [key]: value } : m
      ),
    },
  }));
  if (setHasUnsavedChanges) setHasUnsavedChanges(true);
};

// ============================================================================
// COMPONENTS
// ============================================================================

import generateFormQuestionPrompt from "./component/prompt";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/AlertDialog";
import { useToast } from "@/components/ui/UseToast";
import { Form } from "@/app/types/form";
import { useGetTagByIdQuery, useGetTagsQuery } from "@/services/TagsApi";
import { useGetEventsQuery } from "@/services/EventApi";
import { PathDropdown } from "@/components/ui/PathDropdown";
import { useSelector } from "react-redux";
import { RootState } from "@/app/store";

const AIFormBuilder = ({ prompt, setPrompt, onGenerate, isGenerating }: any) => {
  const handleTranscript = (text: any) => {
    setPrompt((prev: any) => prev + (prev ? " " : "") + text);
  };

  const { isListening, toggleListening, hasSupport } = useSpeechToText({
    onTranscript: handleTranscript,
  });

  return (
    <div className="p-4 border-b">
      <h3 className="font-semibold text-lg mb-3">AI Form Builder</h3>
      <div className="space-y-2">
        <div className="relative">
          <Textarea
            id="prompt"
            placeholder="Describe your form..."
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            rows={4}
            className="text-sm min-h-[100px] pr-12"
          />
          {hasSupport && (
            <Button
              variant="ghost"
              size="icon"
              className={`absolute right-2 top-2 h-8 w-8 transition-colors ${isListening
                ? "text-red-500 hover:text-red-600 bg-red-50 hover:bg-red-100"
                : "text-muted-foreground hover:text-foreground"
                }`}
              onClick={toggleListening}
              title={isListening ? "Stop listening" : "Start voice input"}
              type="button"
            >
              {isListening ? (
                <MicOff className="h-4 w-4 animate-pulse" />
              ) : (
                <Mic className="h-4 w-4" />
              )}
            </Button>
          )}
        </div>
        <Button
          onClick={onGenerate}
          disabled={!prompt.trim() || isGenerating}
          className="w-full bg-primary hover:bg-primary-dark"
          size="sm"
        >
          {isGenerating ? (
            <>
              <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
              Generating...
            </>
          ) : (
            <>
              <Sparkles className="w-3 h-3 mr-2" />
              Generate
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

const FieldComponents = ({ formFields, onDragStart, onDragEnd }: any) => (
  <>
    <div className="p-4 border-b">
      <h3 className="font-semibold text-sm text-gray-600">Field Components</h3>
    </div>
    <div className="flex-1 overflow-auto p-4 space-y-2">
      {formFields?.map((field: any) => (
        <div
          key={field}
          draggable
          onDragStart={(e) => onDragStart(field, e)}
          onDragEnd={onDragEnd}
          className="bg-gray-50 p-2.5 rounded text-sm cursor-move hover:bg-primary hover:text-white border border-gray-200 transition-colors flex items-center gap-2"
        >
          <GripVertical className="w-4 h-4" />
          {capitalize(field)}
        </div>
      ))}
    </div>
  </>
);

const FormPlaceholder = ({ onStartBlank }: any) => (
  <Card className="border-2 border-dashed">
    <CardContent className="pt-6">
      <div className="text-center space-y-4 py-12">
        <Sparkles className="w-16 h-16 text-primary mx-auto" />
        <h3 className="font-semibold text-xl">Create Your Form</h3>
        <p className="text-sm text-gray-600 max-w-md mx-auto">
          Use the AI prompt on the left to generate a form, or click below to
          start with a blank form
        </p>
        <Button
          onClick={onStartBlank}
          className="bg-primary hover:bg-primary-dark"
        >
          <Plus className="w-4 h-4 mr-2" />
          Start Blank Form
        </Button>
      </div>
    </CardContent>
  </Card>
);

const FormHeader = ({
  form,
  setGeneratedFormField,
  editMode,
  onToggleMode,
  onSave,
  isSaving,
  showSave,
  onUpdate,
  isUpdating,
  onBack,
}: {
  form: FromValues;
  setGeneratedFormField: (key: keyof Form, value: any) => void;
  editMode: boolean;
  onToggleMode: () => void;
  onSave: (status?: FormStatus) => Promise<void>;
  isSaving: boolean;
  showSave: boolean;
  onUpdate: (status?: FormStatus) => Promise<void>;
  isUpdating: boolean;
  onBack: () => void;
}) => (
  <CardHeader className="bg-primary text-white">
    <div className="flex items-center justify-between gap-4">
      <Button
        variant="ghost"
        size="icon"
        onClick={onBack}
        className="text-white hover:bg-white/20 hover:text-white shrink-0"
        title="Back to Form Management"
      >
        <ArrowLeft className="w-5 h-5" />
      </Button>
      <div className="flex-1">
        <input
          type="text"
          value={form.name}
          onChange={(e) => setGeneratedFormField('name', e.target.value)}
          className="text-2xl font-bold bg-transparent border-none outline-none text-white placeholder-white/70 w-full"
          placeholder="Form Name"
          maxLength={100}
        />
        <input
          type="text"
          value={form.description}
          onChange={(e) => setGeneratedFormField('description', e.target.value)}
          className="text-sm bg-transparent border-none outline-none text-white/90 placeholder-white/60 w-full mt-1"
          placeholder="Form Description"
        />
      </div>
      <div className="flex gap-2">
        <Button
          onClick={onToggleMode}
          variant="secondary"
          size="sm"
        >
          {editMode ? (
            <Eye className="w-4 h-4 mr-2" />
          ) : (
            <Settings className="w-4 h-4 mr-2" />
          )}
          {editMode ? "Preview" : "Edit"}
        </Button>
        {showSave ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                disabled={isSaving}
                variant="secondary"
                size="sm"
              >
                {isSaving ? (
                  <>
                    <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Form
                    <ChevronDown className="w-4 h-4 ml-1" />
                  </>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onSave()}>
                Save Form
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onSave(FormStatus.Published)}>
                Save and Publish Form
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                onClick={() => onUpdate()}
                disabled={isUpdating}
                variant="secondary"
                size="sm"
              >
                {isUpdating ? (
                  <>
                    <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    Updating...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Update Form
                    <ChevronDown className="w-4 h-4 ml-1" />
                  </>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onUpdate()}>
                Update Form
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onUpdate(FormStatus.Published)}>
                Update and Publish Form
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </div>
  </CardHeader>
);

const FieldRenderer = ({
  field,
  formData,
  isEditing,
  onInputChange,
  onApiTrigger,
}: any) => {
  switch (field.type) {
    case "radio":
      return (
        <RadioGroup
          value={formData[field.id] || ""}
          onValueChange={(value) => {
            onInputChange(field.id, value);
            onApiTrigger(field.id, value);
          }}
          disabled={isEditing}
        >
          {field.options?.map((option: any, idx: number) => (
            <div key={idx} className="flex items-center space-x-2">
              <RadioGroupItem value={option} id={`${field.id}-${idx}`} />
              <Label
                htmlFor={`${field.id}-${idx}`}
                className="font-normal cursor-pointer"
              >
                {option}
              </Label>
            </div>
          ))}
        </RadioGroup>
      );

    case "select":
      return (
        <>
          <Select
            value={formData[field.id] || ""}
            onValueChange={(value) => {
              onInputChange(field.id, value);
              onApiTrigger(field.id, value);
            }}
            disabled={isEditing}
          >
            <SelectTrigger>
              <SelectValue
                placeholder={field.placeholder || "Select an option"}
              />
            </SelectTrigger>
            <SelectContent>
              {field.options?.map((option: any, idx: number) => (
                <SelectItem key={idx} value={option}>
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {field.helpText && (
            <p className="text-sm text-gray-500">{field.helpText}</p>
          )}
        </>
      );

    case "checkbox":
      return (
        <>
          <div className="space-y-2">
            {field.options?.map((option: any, idx: number) => (
              <div key={idx} className="flex items-center space-x-2">
                <Checkbox
                  id={`${field.id}-${idx}`}
                  checked={(formData[field.id] || []).includes(option)}
                  onCheckedChange={(checked) => {
                    const current = formData[field.id] || [];
                    const newValue = checked
                      ? [...current, option]
                      : current.filter((v: any) => v !== option);
                    onInputChange(field.id, newValue);
                    onApiTrigger(field.id, newValue);
                  }}
                  disabled={isEditing}
                />
                <Label
                  htmlFor={`${field.id}-${idx}`}
                  className="font-normal cursor-pointer"
                >
                  {option}
                </Label>
              </div>
            ))}
          </div>
          {field.helpText && (
            <p className="text-sm text-gray-500">{field.helpText}</p>
          )}
        </>
      );

    case "textarea":
      return (
        <>
          <Textarea
            placeholder={field.placeholder}
            value={formData[field.id] || ""}
            onChange={(e) => onInputChange(field.id, e.target.value)}
            onBlur={() => onApiTrigger(field.id, formData[field.id])}
            rows={field.rows || 4}
            className="resize-none"
            disabled={isEditing}
          />
          {field.helpText && (
            <p className="text-sm text-gray-500">{field.helpText}</p>
          )}
        </>
      );

    case "file":
      return (
        <>
          <Input
            type="file"
            placeholder={field.placeholder}
            onChange={(e) => onInputChange(field.id, e.target.files?.[0], true)}
            onBlur={() => onApiTrigger(field.id, formData[field.id]?.name)}
            disabled={isEditing}
            accept={field.accept}
          />
          {formData[field.id] && (
            <p className="text-sm text-gray-500">
              Selected file: {formData[field.id].name}
            </p>
          )}
          {field.helpText && (
            <p className="text-sm text-gray-500">{field.helpText}</p>
          )}
        </>
      );

    default:
      return (
        <>
          <Input
            type={field.type === "datetime" ? "datetime-local" : field.type}
            placeholder={field.placeholder}
            value={formData[field.id] || ""}
            onChange={(e) => onInputChange(field.id, e.target.value)}
            onBlur={() => onApiTrigger(field.id, formData[field.id])}
            disabled={isEditing}
            min={field.min}
            max={field.max}
          />
          {field.helpText && (
            <p className="text-sm text-gray-500">{field.helpText}</p>
          )}
        </>
      );
  }
};

const SortableField = ({
  field,
  index,
  isEditing,
  formData,
  onEdit,
  onDelete,
  onInputChange,
  onApiTrigger,
}: any) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: field.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const { shouldShow, isRequired } = evaluateFieldConditions(field, formData);
  const hasConditions = field.conditions && field.conditions.length > 0;

  if (!shouldShow && !isEditing) return null;

  return (
    <div ref={setNodeRef} style={style} className="relative group">
      {isEditing && (
        <div className="absolute -left-8 top-6 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col gap-2">
          <button
            onClick={(e) => {
              e.stopPropagation()
              onEdit(field) 
            }}
            className="p-1 hover:bg-gray-100 rounded"
            title="Edit Field"
          >
            <Edit2 className="w-4 h-4 text-gray-600" />
          </button>
          <button
            onClick={() => onDelete(field.id)}
            className="p-1 hover:bg-red-50 rounded"

            title="Delete Field"
          >
            <Trash2 className="w-4 h-4 text-red-600" />
          </button>
        </div>
      )}
      <div
        className={`space-y-3 p-6 border rounded-lg bg-white ${isEditing ? "border-primary transition-colors" : ""
          } ${!shouldShow && isEditing ? "opacity-50 border-dashed" : ""}`}
      >
        {hasConditions && isEditing && (
          <div className="flex items-center gap-1 text-xs text-blue-600 mb-2 bg-blue-50 p-2 rounded">
            <Settings className="w-3 h-3" />
            <span>
              {field.conditions.length} conditional rule
              {field.conditions.length > 1 ? "s" : ""}
            </span>
          </div>
        )}

        <div className="flex items-start justify-between gap-2">
          <Label className="text-base font-medium">
            {field.label}
            {isRequired && <span className="text-red-500 ml-1">*</span>}
          </Label>
          {isEditing && (
            <div {...attributes} {...listeners}>
              <GripVertical className="w-4 h-4 text-gray-400 cursor-grab" />
            </div>
          )}
        </div>
        <FieldRenderer
          field={field}
          formData={formData}
          isEditing={isEditing}
          onInputChange={onInputChange}
          onApiTrigger={onApiTrigger}
        />
      </div>
    </div>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const FormBuilder = () => {
  const { userInfo } = useSelector((state: RootState) => state.auth);
  const userOrgId = Number(userInfo?.org_id);
  useDocumentTitle("Form Builder");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const slug = searchParams.get("slug");
  const [prompt, setPrompt] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [sections, setSections] = useState<
    Record<string, { title: string; collapsed: boolean }>
  >({});
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [fieldToDelete, setFieldToDelete] = useState<string | null>(null);
  const { toast } = useToast();

  const { data: formFields } = useGetFormFieldsQuery({});

  const [createForm, { isLoading: isSaving }] = useCreateFormMutation();
  const [updateForm, { isLoading: isUpdating }] = useUpdateFormMutation();
  const [generateAI] = useGenerateAIMutation();

  const {
    eventId,
    setEventId,
    events,
    generatedForm,
    setGeneratedForm,
    formData,
    setFormData,
    editMode,
    setEditMode,
    selectedField,
    setSelectedField,
    isShowSaveForm,
    setIsShowSaveForm,
  } = useFormData(slug, setSections);

  const { data: tagsData } = useGetTagsQuery(
    { org_id: userOrgId, event_ids: eventId ? [eventId] : undefined }, 
  );
  const allTags = tagsData?.data ?? [];
  const groupedTags = useMemo(
    () => (allTags.length > 0 ? createTagTreeFromTagNodes(allTags) : []),
    [allTags]
  );

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const { navigator } = useContext(NavigationContext);
  const [showBlockerDialog, setShowBlockerDialog] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<{ retry: () => void } | null>(null);

  useEffect(() => {
    if (!hasUnsavedChanges) return;

    const push = navigator.push;
    const replace = navigator.replace;

    const handleNavigation = (args: any[], originalFunction: Function) => {
      setShowBlockerDialog(true);
      setPendingNavigation({
        retry: () => {
          setShowBlockerDialog(false);
          setPendingNavigation(null);
          originalFunction(...args);
        }
      });
    };

    navigator.push = (...args: any[]) => handleNavigation(args, push);
    navigator.replace = (...args: any[]) => handleNavigation(args, replace);

    return () => {
      navigator.push = push;
      navigator.replace = replace;
    };
  }, [hasUnsavedChanges, navigator]);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const { draggedFieldType, setDraggedFieldType, sensors } = useDragAndDrop();

  // Sync selectedField changes to generatedForm immediately (Auto-save)
  useEffect(() => {
    if (selectedField) {
      setGeneratedForm((prev: any) => ({
        ...prev,
        fields: prev.fields.map((f: any) =>
          f.id === selectedField.id ? selectedField : f
        ),
      }));
    }
  }, [selectedField, setGeneratedForm]);

  // Field Management Functions
  const handleEditField = (field: any) => {
    setSelectedField({ ...field, apiIntegration: { ...field.apiIntegration } });
  };

  const handleDeleteField = (fieldId: string) => {
    setGeneratedForm((prev: any) => {
      if (!prev) return prev;

      // First, get the fields before deletion
      const remainingFields = prev.fields.filter((f: any) => f.id !== fieldId);

      // Clean up any conditions that referenced the deleted field
      const cleanedFields = remainingFields.map((field: any) => {
        if (!field.conditions || field.conditions.length === 0) return field;

        const validConditions = field.conditions.filter(
          (cond: any) => cond.sourceField !== fieldId
        );

        return {
          ...field,
          conditions: validConditions.length > 0 ? validConditions : undefined,
        };
      });

      return {
        ...prev,
        fields: cleanedFields,
      };
    });
    setHasUnsavedChanges(true);

    if (selectedField?.id === fieldId) {
      setSelectedField(null);
    }
  };

  const handleAddField = () => {
    const newField: Field = {
      id: `field_${Date.now()}`,
      type: "text",
      label: "New Field",
      placeholder: "",
      helpText: "",
      required: false,
      options: [],
      apiIntegration: {
        enabled: false,
        endpoint: "",
        method: "GET",
        token: "",
        responseMappings: [],
      },
    };
    setGeneratedForm((prev: any) => ({
      ...prev,
      fields: [...prev.fields, newField],
    }));
    setHasUnsavedChanges(true);
    setSelectedField(newField);
  };



  const handleFieldChange = (key: string, value: any) => {
    setSelectedField((prev: any) => ({
      ...prev,
      [key]: value,
    }));
    setHasUnsavedChanges(true);
  };

  // AI Generation
  const handleGenerateForm = async () => {
    try {
      setIsGenerating(true);
      const formID = generatedForm?.id || null;
      const formName = generatedForm?.name || "";
      const formDescription = generatedForm?.description || "";
      const currentFields = generatedForm?.fields || [];

      const promptText = generateFormQuestionPrompt(
        prompt,
        formName,
        formDescription,
        currentFields,
      );

      const aiResponse = await generateAI({ type: "form", user_prompt: promptText }).unwrap();

      const {
        actionType,
        fields: newFields,
        name: newName,
        description: newDescription
      }: any = aiResponse;

      // Normalize all AI-generated fields to ensure consistent property naming
      const normalizedFields = (newFields || []).map(normalizeAIField);

      setGeneratedForm((prev: any) => {
        let updatedForm;

        if (actionType === "addQuestions") {
          const validatedFields = validateFieldConditions(
            normalizedFields,
            prev?.fields || []
          );
          updatedForm = {
            ...prev,
            fields: [...(prev?.fields || []), ...validatedFields],
          };
        } else if (actionType === "createForm") {
          updatedForm = {
            name: newName || "New Form",
            description: newDescription || "",
            fields: normalizedFields,
            id: formID
          };
        } else {
          return prev;
        }

        // Update sections state based on the new/updated fields
        const sectionsMap: Record<string, { title: string; collapsed: boolean }> = {};
        updatedForm.fields.forEach((field: any) => {
          if (field.sectionId && !sectionsMap[field.sectionId]) {
            sectionsMap[field.sectionId] = {
              title: field.sectionTitle || "Untitled Section",
              collapsed: false,
            };
          }
        });
        setSections(sectionsMap);

        return updatedForm;
      });

      setSelectedField(null);
      setHasUnsavedChanges(true);
    } catch (error: any) {
      console.error("Generation error:", error);
      toast({
        variant: "destructive",
        title: "Generation Failed",
        description: error.message || "Failed to generate form. Please try again.",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  // Form Actions
  const handleSaveForm = async (status?: FormStatus) => {
    if (!generatedForm) return;

    if (!generatedForm.name.trim()) {
      toast({
        variant: "destructive",
        title: "Validation Error",
        description: "Please enter a form name.",
      });
      return;
    }

    if (!generatedForm.tag_path) {
      toast({
        variant: "destructive",
        title: "Validation Error",
        description: "Please select a service path",
      });
      return;
    }

    try {
      const payload: any = {
        name: generatedForm.name,
        description: generatedForm.description,
        tag_path: '/' + generatedForm.tag_path.join('/'),
        requires_login: generatedForm.requires_login,
        show_on_user_portal: generatedForm.show_on_user_portal,
        fields: generatedForm.fields.map((field: any, index: number) => ({
          ...field,
          order: index,
          field_id: field.id,
          api_integration: field.apiIntegration,
          section_id: field.sectionId,
          section_title: field.sectionTitle,
          start_date_time: field.startDateTime,
          end_date_time: field.endDateTime,
          layout: field.layout,
        })),
        status: status || FormStatus.Draft,
      };
      const res = await createForm(payload).unwrap();
      if (res) {
        setGeneratedForm({
          ...generatedForm,
          id: res.id,
        });
        toast({
          variant: "success",
          title: "Success",
          description: "Form saved successfully!",
        });
        setHasUnsavedChanges(false);
        setTimeout(() => {
          navigate("/admin/form-management");
        }, 0);
      }
      setIsShowSaveForm(false);
    } catch (error) {
      console.error("Save error:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: `Failed to save form. ${(error as any)?.data?.message || "Unknown error"}.`,
      });
    }
  };

  const handleUpdateForm = async (status?: FormStatus) => {
    if (!generatedForm) return;

    if (!generatedForm.name.trim()) {
      toast({
        variant: "destructive",
        title: "Validation Error",
        description: "Please enter a form name.",
      });
      return;
    }

    try {
      const payload: any = {
        id: generatedForm.id,
        name: generatedForm.name,
        description: generatedForm.description,
        fields: generatedForm.fields.map((field: any, index: number) => ({
          ...field,
          order: index,
          field_id: field.id,
          api_integration: field.apiIntegration,
          section_id: field.sectionId,
          section_title: field.sectionTitle,
          start_date_time: field.startDateTime,
          end_date_time: field.endDateTime,
          layout: field.layout,
          id: undefined,
        })),
        status: status,
      };
      await updateForm(payload).unwrap();
      toast({
        variant: "success",
        title: "Success",
        description: "Form updated successfully!",
      });
      setHasUnsavedChanges(false);
      setTimeout(() => {
        navigate("/admin/form-management");
      }, 0);
    } catch (error) {
      console.error("Update error:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: `Failed to update form. ${(error as any)?.data?.message || "Unknown error"}.`,
      });
    }
  };

  const handleInputChange = (fieldId: string, value: any, isFile = false) => {
    setFormData((prev) => ({
      ...prev,
      [fieldId]: value,
    }));
  };

  const handleApiTrigger = async (fieldId: string, value: any) => {
    const field = generatedForm?.fields.find((f: any) => f.id === fieldId);
    if (!field?.apiIntegration?.enabled || !value) return;

    try {
      const endpoint = field.apiIntegration.endpoint.replace(
        "{value}",
        encodeURIComponent(value)
      );
      const response = await fetch(endpoint, {
        method: field.apiIntegration.method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("userToken") ?? ""}`,
        },
      });

      if (!response.ok) throw new Error("API error");
      const data = await response.json();

      const newFormData = { ...formData };
      field.apiIntegration.responseMappings.forEach((mapping: any) => {
        if (mapping.targetField) {
          const val = Array.isArray(data)
            ? data[0]?.[mapping.responsePath]
            : data?.[mapping.responsePath];
          if (val !== undefined) {
            newFormData[mapping.targetField] = val;
          }
        }
      });
      setFormData(newFormData);
    } catch (error) {
      console.error("API integration error:", error);
    }
  };

  const handleDragEnd = (event: any) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      setGeneratedForm((prev: any) => {
        const fields = prev.fields;
        const oldIndex = fields.findIndex((f: any) => f.id === active.id);
        const newIndex = fields.findIndex((f: any) => f.id === over.id);
        return {
          ...prev,
          fields: arrayMove(fields, oldIndex, newIndex),
        };
      });
      setHasUnsavedChanges(true);
    }
  };

  const confirmDeleteField = () => {
    if (fieldToDelete) {
      handleDeleteField(fieldToDelete);
      setFieldToDelete(null);
    }
    setDeleteDialogOpen(false);
  };

  const setGeneratedFormField = (key: keyof Form, value: any) => {
    setGeneratedForm((prev: any) => ({ ...prev, [key]: value }));
    setHasUnsavedChanges(true);
  }

  return (
    <div className="w-full h-[calc(100vh-64px)] bg-gray-50 flex overflow-hidden" onClick={() => setSelectedField(null)}>
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Field</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this field?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-row justify-start gap-2">
            <AlertDialogCancel className="w-24 m-0">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteField}
              className="w-24 bg-red-600 hover:bg-red-700 m-0"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Unsaved Changes Blocker Dialog */}
      <AlertDialog open={showBlockerDialog} onOpenChange={setShowBlockerDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <div className="flex items-center gap-3">
              <Settings className="h-6 w-6 text-amber-600" />
              <AlertDialogTitle>Unsaved Changes</AlertDialogTitle>
            </div>
            <AlertDialogDescription>
              You have unsaved changes. Are you sure you want to leave? Your changes will be lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="w-30" onClick={() => {
              setShowBlockerDialog(false);
              setPendingNavigation(null);
            }}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => {
              if (pendingNavigation) {
                setHasUnsavedChanges(false);
                // Use setTimeout to ensure state update is processed before navigation
                setTimeout(() => {
                  pendingNavigation.retry();
                }, 0);
              }
            }}>Continue</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Left Sidebar */}
      <div className="w-64 border-r bg-white lg:flex flex-col md:hidden">
        <AIFormBuilder
          prompt={prompt}
          setPrompt={setPrompt}
          onGenerate={handleGenerateForm}
          isGenerating={isGenerating}
        />
        <FieldComponents
          formFields={formFields}
          onDragStart={(field: any, e: any) => {
            setDraggedFieldType(field);
            e.dataTransfer.effectAllowed = "copy";
          }}
          onDragEnd={() => setDraggedFieldType(null)}
        />
      </div>

      {/* Main Canvas */}
      <div className="flex-1 overflow-y-auto bg-gray-50">
        <div className="mx-auto p-4">
          {generatedForm ? (
            <Card>
              <FormHeader
                form={generatedForm}
                setGeneratedFormField={setGeneratedFormField}
                editMode={editMode}
                onToggleMode={() => setEditMode(!editMode)}
                onSave={handleSaveForm}
                isSaving={isSaving}
                showSave={isShowSaveForm}
                onUpdate={handleUpdateForm}
                isUpdating={isUpdating}
                onBack={() => navigate("/admin/form-management")}
              />
              <CardContent
                className="pt-6 min-h-[400px]"
                onDragOver={(e) => {
                  e.preventDefault();
                  e.dataTransfer.dropEffect = "copy";
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  if (draggedFieldType && editMode) {
                    const newField: Field = {
                      id: `field_${Date.now()}`,
                      type: draggedFieldType,
                      label: `New ${capitalize(draggedFieldType)} Field`,
                      placeholder: "",
                      helpText: "",
                      required: false,
                      options: ["radio", "select", "checkbox"].includes(
                        draggedFieldType
                      )
                        ? ["Option 1", "Option 2"]
                        : undefined,
                      min:
                        draggedFieldType === "number" ? undefined : undefined,
                      max:
                        draggedFieldType === "number" ? undefined : undefined,
                      rows: draggedFieldType === "textarea" ? 3 : undefined,
                      accept:
                        draggedFieldType === "file"
                          ? ".pdf,.doc,.docx"
                          : undefined,
                      apiIntegration: {
                        enabled: false,
                        endpoint: "",
                        method: "GET",
                        token: "",
                        responseMappings: [],
                      },
                    };
                    setGeneratedForm((prev: any) => ({
                      ...prev,
                      fields: [...prev.fields, newField],
                    }));
                    setHasUnsavedChanges(true);
                    setDraggedFieldType(null);
                  }
                }}
              >
                {generatedForm.fields.length === 0 ? (
                  <div className="flex items-center justify-center h-[400px] border-2 border-dashed border-gray-300 rounded-lg">
                    <div className="text-center space-y-2">
                      <FileText className="w-12 h-12 text-gray-400 mx-auto" />
                      <h3 className="font-semibold text-lg text-gray-600">
                        {editMode ? "Drag & Drop Fields Here" : "No Fields Yet"}
                      </h3>
                      <p className="text-sm text-gray-500">
                        {editMode
                          ? "Drag components from the left sidebar to build your form"
                          : "Switch to Edit mode to add fields"}
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-6 pl-8">
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragEnd={handleDragEnd}
                    >
                      <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handleDragEnd}
                      >
                        <SortableContext
                          items={generatedForm.fields.map((f: any) => f.id)}
                          strategy={verticalListSortingStrategy}
                        >
                          {(() => {
                            // Group fields by section while preserving order
                            const sectionOrder: string[] = [];
                            const groupedBySection =
                              generatedForm.fields.reduce((acc: any, field: any) => {
                                const key = field.sectionId || "ungrouped";
                                if (!acc[key]) {
                                  acc[key] = {
                                    title:
                                      field.sectionTitle ||
                                      sections[key]?.title ||
                                      "Untitled Section",
                                    fields: [],
                                  };
                                  if (!sectionOrder.includes(key)) {
                                    sectionOrder.push(key);
                                  }
                                }
                                acc[key].fields.push(field);
                                return acc;
                              }, {} as Record<string, { title: string; fields: Field[] }>);

                            // Ensure ungrouped fields appear last if they exist
                            if (sectionOrder.includes("ungrouped")) {
                              sectionOrder.push(
                                sectionOrder.splice(
                                  sectionOrder.indexOf("ungrouped"),
                                  1
                                )[0]
                              );
                            }

                            return Object.entries(groupedBySection)
                              .sort(([keyA], [keyB]) => {
                                return (
                                  sectionOrder.indexOf(keyA) -
                                  sectionOrder.indexOf(keyB)
                                );
                              })
                              .map(([sectionId, group]: any) => {
                                const isCollapsed =
                                  sections[sectionId]?.collapsed ?? false;

                                return (
                                  <div key={sectionId} className="mb-12">
                                    {/* Section Header */}
                                    {sectionId !== "ungrouped" && (
                                      <div
                                        className="flex items-center justify-between p-5 bg-primary/5 rounded-lg cursor-pointer hover:bg-primary/10 transition mb-6 border border-primary/20 select-none"
                                        onClick={() =>
                                          setSections((prev) => ({
                                            ...prev,
                                            [sectionId]: {
                                              ...prev[sectionId],
                                              collapsed:
                                                !prev[sectionId]?.collapsed,
                                            },
                                          }))
                                        }
                                      >
                                        <h3 className="font-semibold text-lg text-primary">
                                          {group.title}
                                        </h3>
                                        <span className="text-sm text-gray-600">
                                          {group.fields.length} field
                                          {group.fields.length !== 1 ? "s" : ""}
                                          {isCollapsed ? " ▸" : " ▾"}
                                        </span>
                                      </div>
                                    )}

                                    {!isCollapsed && (
                                      <div
                                        className={
                                          sectionId !== "ungrouped"
                                            ? "pl-8"
                                            : ""
                                        }
                                      >
                                        {/* Row Grouping Inside Section */}
                                        {(() => {
                                          const rows: Field[][] = [];
                                          let currentRow: Field[] = [];

                                          group.fields.forEach((field: any) => {
                                            const cols =
                                              field.layout?.columns || 1;

                                            if (cols === 1) {
                                              if (currentRow.length > 0) {
                                                rows.push(currentRow);
                                                currentRow = [];
                                              }
                                              rows.push([field]);
                                            } else {
                                              if (
                                                currentRow.length > 0 &&
                                                currentRow[0].layout
                                                  ?.columns !== cols
                                              ) {
                                                rows.push(currentRow);
                                                currentRow = [];
                                              }
                                              currentRow.push(field);
                                              if (currentRow.length === cols) {
                                                rows.push(currentRow);
                                                currentRow = [];
                                              }
                                            }
                                          });
                                          if (currentRow.length > 0)
                                            rows.push(currentRow);

                                          return rows.map((row, rowIndex) => {
                                            const columnCount =
                                              row[0]?.layout?.columns || 1;

                                            return (
                                              <div
                                                key={rowIndex}
                                                className={`grid gap-6 mb-6 ${columnCount === 1
                                                  ? "grid-cols-1"
                                                  : columnCount === 2
                                                    ? "grid-cols-1 md:grid-cols-2"
                                                    : columnCount === 3
                                                      ? "grid-cols-1 md:grid-cols-3"
                                                      : "grid-cols-1 md:grid-cols-2 lg:grid-cols-4"
                                                  }`}
                                              >
                                                {row.map((field) => (
                                                  <SortableField
                                                    key={field.id}
                                                    field={field}
                                                    isEditing={editMode}
                                                    formData={formData}
                                                    onEdit={handleEditField}
                                                    onDelete={() => {
                                                      setFieldToDelete(field.id);
                                                      setDeleteDialogOpen(true);
                                                    }}
                                                    onInputChange={
                                                      handleInputChange
                                                    }
                                                    onApiTrigger={
                                                      handleApiTrigger
                                                    }
                                                  />
                                                ))}

                                                {/* Fill empty cells */}
                                                {columnCount > row.length &&
                                                  Array(
                                                    columnCount - row.length
                                                  )
                                                    .fill(null)
                                                    .map((_, i) => (
                                                      <div key={`empty-${i}`} />
                                                    ))}
                                              </div>
                                            );
                                          });
                                        })()}
                                      </div>
                                    )}
                                  </div>
                                );
                              });
                          })()}
                        </SortableContext>
                      </DndContext>
                    </DndContext>

                    {editMode && (
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center text-gray-500 hover:border-primary hover:bg-primary/5 transition-colors">
                        Drop field here or click below to add
                        <Button
                          onClick={handleAddField}
                          variant="outline"
                          className="w-full mt-3"
                          size="sm"
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          Add Field Manually
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          ) : (
            <FormPlaceholder
              onStartBlank={() =>
                setGeneratedForm(initialForm)
              }
            />
          )}
        </div>
      </div>

      {/* Right Sidebar */}
      <div className="w-64 border-l bg-white lg:flex flex-col md:hidden" onClick={(e) => e.stopPropagation()}>

        {selectedField ? (
          <>
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="font-semibold text-lg">Field Editor</h3>
            </div>
            <div className="flex-1 overflow-auto p-4 space-y-4">
              <div className="space-y-2">
                <Label>Field Type</Label>
                <select
                  value={selectedField.type}
                  onChange={(e) => handleFieldChange("type", e.target.value)}
                  className="w-full p-2 border rounded"
                >
                  {formFields?.map((fieldType) => (
                    <option key={fieldType} value={fieldType}>
                      {capitalize(fieldType)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <Label>Label</Label>
                <Input
                  value={selectedField.label}
                  onChange={(e) => handleFieldChange("label", e.target.value)}
                  placeholder="Enter field label"
                />
              </div>

              <div className="space-y-2">
                <Label>Placeholder</Label>
                <Input
                  value={selectedField.placeholder || ""}
                  onChange={(e) =>
                    handleFieldChange("placeholder", e.target.value)
                  }
                  placeholder="Enter placeholder text"
                />
              </div>

              <div className="space-y-2">
                <Label>Help Text</Label>
                <Textarea
                  value={selectedField.helpText || ""}
                  onChange={(e) =>
                    handleFieldChange("helpText", e.target.value)
                  }
                  placeholder="Enter help text"
                  rows={2}
                />
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="required"
                  checked={selectedField.required || false}
                  onChange={(e) =>
                    handleFieldChange("required", e.target.checked)
                  }
                  className="rounded"
                />
                <Label htmlFor="required">Required field</Label>
              </div>

              {["radio", "select", "checkbox"].includes(selectedField.type) && (
                <div className="space-y-2">
                  <Label>Options</Label>
                  <div className="space-y-2">
                    {(selectedField.options || []).map((option, idx) => (
                      <div key={idx} className="flex gap-2">
                        <Input
                          value={option}
                          onChange={(e) => {
                            const newOptions = [
                              ...(selectedField.options || []),
                            ];
                            newOptions[idx] = e.target.value;
                            handleFieldChange("options", newOptions);
                          }}
                          placeholder={`Option ${idx + 1}`}
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            const newOptions = (
                              selectedField.options || []
                            ).filter((_, i) => i !== idx);
                            handleFieldChange("options", newOptions);
                          }}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                    <Button
                      type="button" 
                      onClick={() => {
                        const newOptions = [
                          ...(selectedField.options || []),
                          `Option ${(selectedField.options || []).length}`,
                        ];
                        handleFieldChange("options", newOptions);
                      }}
                      variant="outline"
                      size="sm"
                      className="w-full"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Option
                    </Button>
                  </div>
                </div>
              )}

              {selectedField.type === "textarea" && (
                <div className="space-y-2">
                  <Label>Rows</Label>
                  <Input
                    type="number"
                    value={selectedField.rows || 3}
                    onChange={(e) =>
                      handleFieldChange("rows", parseInt(e.target.value))
                    }
                  />
                </div>
              )}

              {(selectedField.type === "number" || selectedField.type === "text") && (
                <>
                  <div className="space-y-2">
                    <Label>Min</Label>
                    <Input
                      type="number"
                      value={selectedField.min || ""}
                      onChange={(e) =>
                        handleFieldChange(
                          "min",
                          e.target.value ? parseInt(e.target.value) : undefined
                        )
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Max</Label>
                    <Input
                      type="number"
                      value={selectedField.max || ""}
                      onChange={(e) =>
                        handleFieldChange(
                          "max",
                          e.target.value ? parseInt(e.target.value) : undefined
                        )
                      }
                    />
                  </div>
                </>
              )}

              {(selectedField.type === "date" ||
                selectedField.type === "datetime") && (
                  <>
                    <div className="space-y-2">
                      <Label>{selectedField.type === "date" ? "Min Date" : "Min Date Time"}</Label>
                      <Input
                        type={selectedField.type === "date" ? "date" : "datetime-local"}
                        value={selectedField.startDateTime && selectedField.type === "date" ? selectedField.startDateTime.split("T")[0] : selectedField.startDateTime || ""}
                        onChange={(e) =>
                          handleFieldChange("startDateTime", e.target.value)
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>{selectedField.type === "date" ? "Max Date" : "Max Date Time"}</Label>
                      <Input
                        type={selectedField.type === "date" ? "date" : "datetime-local"}
                        value={selectedField.endDateTime && selectedField.type === "date" ? selectedField.endDateTime.split("T")[0] : selectedField.endDateTime || ""}
                        onChange={(e) =>
                          handleFieldChange("endDateTime", e.target.value)
                        }
                      />
                    </div>
                  </>
                )}

              {selectedField.type === "file" && (
                <div className="space-y-2">
                  <Label>Accepted File Types</Label>
                  <Input
                    value={selectedField.accept || ""}
                    onChange={(e) =>
                      handleFieldChange("accept", e.target.value)
                    }
                    placeholder=".pdf,.jpg"
                  />
                </div>
              )}

              {/* GROUPING SECTION */}
              <div className="pt-4 border-t space-y-3">
                <Label className="text-base font-semibold">
                  Section / Grouping
                </Label>

                <Select
                  value={selectedField?.sectionId || ""}
                  onValueChange={(value) => {
                    if (value === "none") {
                      handleFieldChange("sectionId", undefined);
                      handleFieldChange("sectionTitle", undefined);
                    } else if (value === "new") {
                      const newId = `sec_${Date.now()}`;
                      setSections((prev) => ({
                        ...prev,
                        [newId]: { title: "New Section", collapsed: false },
                      }));
                      setHasUnsavedChanges(true);
                      handleFieldChange("sectionId", newId);
                      handleFieldChange("sectionTitle", "New Section");
                    } else {
                      const sectionTitle = sections[value]?.title || "Section";
                      handleFieldChange("sectionId", value);
                      handleFieldChange("sectionTitle", sectionTitle);
                    }
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="No section (default)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No section</SelectItem>
                    <SelectItem value="new">+ Create new section</SelectItem>
                    {Object.entries(sections).map(([id, sec]) => (
                      <SelectItem key={id} value={id}>
                        {sec.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {selectedField?.sectionId && (
                  <div className="space-y-2">
                    <Label>Section Title</Label>
                    <Input
                      value={selectedField.sectionTitle || ""}
                      onChange={(e) => {
                        const newTitle = e.target.value || "Untitled Section";
                        handleFieldChange("sectionTitle", newTitle);
                        setSections((prev) => ({
                          ...prev,
                          [selectedField.sectionId!]: {
                            ...prev[selectedField.sectionId!],
                            title: newTitle,
                          },
                        }));
                        setHasUnsavedChanges(true);
                      }}
                      placeholder="Section title"
                    />
                  </div>
                )}
              </div>

              {/* === MULTI-COLUMN LAYOUT (ADD THIS) === */}
              <div className="pt-4 border-t space-y-3">
                <Label className="text-base font-semibold">
                  Layout (Multi-column)
                </Label>

                <Select
                  value={(selectedField?.layout?.columns || 1).toString()}
                  onValueChange={(value) => {
                    const cols = parseInt(value);
                    handleFieldChange("layout", {
                      columns: cols,
                      order: selectedField?.layout?.order || 0,
                    });
                  }}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">Single column (default)</SelectItem>
                    <SelectItem value="2">2 columns</SelectItem>
                    <SelectItem value="3">3 columns</SelectItem>
                    <SelectItem value="4">4 columns</SelectItem>
                  </SelectContent>
                </Select>

                {selectedField?.layout?.columns &&
                  selectedField.layout.columns > 1 && (
                    <div className="text-sm text-gray-600 bg-blue-50 p-3 rounded">
                      This field shares a row with{" "}
                      {selectedField.layout.columns - 1} other field(s).
                      <br />
                      <strong>Tip:</strong> Set the same “columns” value on
                      adjacent fields.
                    </div>
                  )}
              </div>

              {/* CONDITIONAL LOGIC SECTION */}
              <div className="pt-4 border-t space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-semibold mr-3">
                    Conditions
                  </Label>
                  <Button
                    onClick={() => handleAddCondition(setSelectedField, setHasUnsavedChanges)}
                    variant="outline"
                    size="sm"
                  >
                    <Plus className="w-3 h-3 mr-1" />
                    Add Rule
                  </Button>
                </div>

                {(selectedField.conditions || []).length === 0 ? (
                  <p className="text-sm text-gray-500">
                    No conditional rules set
                  </p>
                ) : (
                  <div className="space-y-3">
                    {(selectedField.conditions || []).map((condition, idx) => (
                      <div
                        key={condition.id}
                        className="p-3 border rounded-lg bg-gray-50 space-y-2"
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-medium text-gray-600">
                            Rule {idx + 1}
                          </span>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() =>
                              handleRemoveCondition(
                                condition.id,
                                setSelectedField,
                                setHasUnsavedChanges
                              )
                            }
                          >
                            <Trash2 className="w-3 h-3 text-red-600" />
                          </Button>
                        </div>

                        <div className="space-y-2">
                          <Label className="text-xs">Action</Label>
                          <select
                            value={condition.action}
                            onChange={(e) =>
                              handleConditionChange(
                                condition.id,
                                "action",
                                e.target.value,
                                setSelectedField,
                                setHasUnsavedChanges
                              )
                            }
                            className="w-full p-2 text-sm border rounded"
                          >
                            <option value="show">Show this field</option>
                            <option value="hide">Hide this field</option>
                            <option value="require">Make required</option>
                            <option value="optional">Make optional</option>
                          </select>
                        </div>

                        <div className="space-y-2">
                          <Label className="text-xs">When field</Label>
                          <select
                            value={condition.sourceField}
                            onChange={(e) =>
                              handleConditionChange(
                                condition.id,
                                "sourceField",
                                e.target.value,
                                setSelectedField,
                                setHasUnsavedChanges
                              )
                            }
                            className="w-full p-2 text-sm border rounded"
                          >
                            <option value="">Select field...</option>
                            {generatedForm?.fields
                              .filter((f: any) => f.id !== selectedField.id)
                              .map((field: any) => (
                                <option key={field.id} value={field.id}>
                                  {field.label}
                                </option>
                              ))}
                          </select>
                        </div>

                        <div className="space-y-2">
                          <Label className="text-xs">Operator</Label>
                          <select
                            value={condition.operator}
                            onChange={(e) =>
                              handleConditionChange(
                                condition.id,
                                "operator",
                                e.target.value,
                                setSelectedField,
                                setHasUnsavedChanges
                              )
                            }
                            className="w-full p-2 text-sm border rounded"
                          >
                            <option value="equals">Equals</option>
                            <option value="not_equals">Not equals</option>
                            <option value="includes">Includes</option>
                            <option value="not_includes">
                              Does not include
                            </option>
                            <option value="is_empty">Is empty</option>
                            <option value="is_not_empty">Is not empty</option>
                          </select>
                        </div>

                        {!["is_empty", "is_not_empty"].includes(
                          condition.operator
                        ) && (
                            <div className="space-y-2">
                              <Label className="text-xs">Value</Label>
                              <Input
                                value={condition.value}
                                onChange={(e) =>
                                  handleConditionChange(
                                    condition.id,
                                    "value",
                                    e.target.value,
                                    setSelectedField,
                                    setHasUnsavedChanges
                                  )
                                }
                                placeholder="Enter value"
                                className="text-sm"
                              />
                            </div>
                          )}
                      </div>
                    ))}
                  </div>
                )}

                <div className="text-xs text-gray-500 bg-blue-50 p-2 rounded">
                  💡 Conditional logic controls when this field appears or if
                  it's required based on other field values
                </div>
              </div>

              {/* API INTEGRATION SECTION */}
              <div className="pt-4 border-t space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-semibold mr-3">
                    API Integration
                  </Label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="api-enabled"
                      checked={selectedField.apiIntegration?.enabled || false}
                      onChange={(e) =>
                        handleToggleApi(e.target.checked, setSelectedField, setHasUnsavedChanges)
                      }
                      className="rounded"
                    />
                    <Label htmlFor="api-enabled">Enable</Label>
                  </div>
                </div>

                {selectedField.apiIntegration?.enabled && (
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label>Endpoint Source</Label>
                      <Select
                        value={
                          selectedField.apiIntegration?.endpoint ===
                            ITS_URL
                            ? "its"
                            : "custom"
                        }
                        onValueChange={(value) => {
                          if (value === "its") {
                            handleApiChange(
                              "endpoint",
                              ITS_URL,
                              setSelectedField,
                              setHasUnsavedChanges
                            );
                          } else {
                            handleApiChange("endpoint", "", setSelectedField, setHasUnsavedChanges);
                          }
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select source" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="its">
                            ITS Number Endpoint
                          </SelectItem>
                          <SelectItem value="custom">Custom URL</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {selectedField.apiIntegration?.endpoint !==
                      ITS_URL && (
                        <div className="space-y-2">
                          <Label>API Endpoint</Label>
                          <Input
                            value={selectedField.apiIntegration?.endpoint || ""}
                            onChange={(e) =>
                              handleApiChange(
                                "endpoint",
                                e.target.value,
                                setSelectedField,
                                setHasUnsavedChanges
                              )
                            }
                            placeholder="https://api.example.com/its/{value}"
                          />
                          <p className="text-xs text-gray-500">{`Use {value} for field input`}</p>
                        </div>
                      )}

                    <div className="space-y-2">
                      <Label>Method</Label>
                      <select
                        value={selectedField.apiIntegration?.method || "GET"}
                        onChange={(e) =>
                          handleApiChange(
                            "method",
                            e.target.value,
                            setSelectedField,
                            setHasUnsavedChanges
                          )
                        }
                        className="w-full p-2 border rounded"
                      >
                        <option value="GET">GET</option>
                        <option value="POST">POST</option>
                      </select>
                    </div>

                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <Label className="text-sm">Response Mappings</Label>
                        <Button
                          onClick={() => handleAddMapping(setSelectedField, setHasUnsavedChanges)}
                          variant="outline"
                          size="sm"
                        >
                          <Plus className="w-3 h-3 mr-1" />
                          Add Mapping
                        </Button>
                      </div>

                      {(selectedField.apiIntegration?.responseMappings || [])
                        .length === 0 ? (
                        <p className="text-sm text-gray-500">No mappings set</p>
                      ) : (
                        <div className="space-y-3">
                          {(
                            selectedField.apiIntegration?.responseMappings || []
                          ).map((mapping, idx) => (
                            <div
                              key={mapping.id}
                              className="p-3 border rounded-lg bg-gray-50 space-y-2"
                            >
                              <div className="flex items-center justify-between">
                                <span className="text-xs font-medium text-gray-600">
                                  Mapping {idx + 1}
                                </span>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() =>
                                    handleRemoveMapping(
                                      mapping.id,
                                      setSelectedField,
                                      setHasUnsavedChanges
                                    )
                                  }
                                >
                                  <Trash2 className="w-3 h-3 text-red-600" />
                                </Button>
                              </div>

                              <div className="space-y-2">
                                <Label className="text-xs">Response Path</Label>
                                {selectedField.apiIntegration?.endpoint ===
                                  ITS_URL ? (
                                  <Select
                                    value={mapping.responsePath}
                                    onValueChange={(value) =>
                                      handleMappingChange(
                                        mapping.id,
                                        "responsePath",
                                        value,
                                        setSelectedField,
                                        setHasUnsavedChanges
                                      )
                                    }
                                  >
                                    <SelectTrigger>
                                      <SelectValue placeholder="Select field" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {ITS_FIELDS.map((field) => (
                                        <SelectItem key={field.name} value={field.name}>
                                          {field.label}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                ) : (
                                  <Input
                                    value={mapping.responsePath}
                                    onChange={(e) =>
                                      handleMappingChange(
                                        mapping.id,
                                        "responsePath",
                                        e.target.value,
                                        setSelectedField,
                                        setHasUnsavedChanges
                                      )
                                    }
                                    placeholder="variable name"
                                    className="text-sm"
                                  />
                                )}
                                {selectedField.apiIntegration?.endpoint !==
                                  ITS_URL && (
                                    <p className="text-[11px] text-gray-500">
                                      Wrap in double quotes to use a static value,
                                      e.g. "Approved".
                                    </p>
                                  )}
                              </div>

                              <div className="space-y-2">
                                <Label className="text-xs">Target Field</Label>
                                <select
                                  value={mapping.targetField}
                                  onChange={(e) =>
                                    handleMappingChange(
                                      mapping.id,
                                      "targetField",
                                      e.target.value,
                                      setSelectedField,
                                      setHasUnsavedChanges
                                    )
                                  }
                                  className="w-full p-2 text-sm border rounded"
                                >
                                  <option value="">Select field...</option>
                                  {generatedForm?.fields
                                    .filter((f: any) => f.id !== selectedField.id)
                                    .map((field: any) => (
                                      <option key={field.id} value={field.id}>
                                        {field.label}
                                      </option>
                                    ))}
                                </select>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      <div className="text-xs text-gray-500 bg-blue-50 p-2 rounded">
                        💡 API calls on field change to fill other fields based
                        on response
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>


          </>
        ) : (
          <>
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="font-semibold text-lg">Form Settings</h3>
            </div>
            <div className="flex-1 flex flex-col gap-4 p-4 text-center text-gray-500">
              <Select
                onValueChange={(val) => {
                  const prevEventId = eventId;
                  setEventId(Number(val));
                  if (!prevEventId) return;
                  setGeneratedFormField("tag_path", undefined);
                }}
                value={eventId ? eventId.toString() : undefined}
              >
                <SelectTrigger
                  className="w-full bg-white"
                  floatingLabel="Event"
                  labelClassname="bg-white"
                >
                  <SelectValue placeholder="Select Event" />
                </SelectTrigger>
                <SelectContent>
                  {events.map((event) => (
                    <SelectItem
                      key={event.event_id}
                      value={event.event_id.toString()}
                    >
                      {event.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <PathDropdown
                value={generatedForm?.tag_path}
                onChange={(path) => {
                  if (!path || !path.length) return;
                  setGeneratedFormField("tag_path", path);
                }}
                groups={groupedTags}
                label="Service"
                placeholder="Select Service"
                placeholderClassname="text-xs"
                searchPlaceholder="Search tags..."
                noResultsText="No Service found."
                className="col-span-1 md:col-span-2"
                listItemClassname="text-xs"
              />
              <div className="flex items-center gap-2 ml-2">
                <label className="font-semibold text-gray-600 text-xs">Requires Login: </label>
                <Switch
                  checked={generatedForm?.requires_login}
                  onCheckedChange={(checked) => {
                    setGeneratedFormField("requires_login", checked);
                  }}
                />
              </div>
              <div className="flex items-center gap-2 ml-2">
                <label className="font-semibold text-gray-600 text-xs">Show on user portal: </label>
                <Switch
                  checked={generatedForm?.show_on_user_portal}
                  onCheckedChange={(checked) => {
                    setGeneratedFormField("show_on_user_portal", checked);
                  }}
                />
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default FormBuilder;
